import { motion } from "framer-motion";
import { Check, ArrowUpRight, Globe } from "lucide-react";
import { PRICING } from "@/lib/site";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Pricing = () => {
  return (
    <section id="pricing" data-testid="pricing-section" className="relative bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-14 max-w-2xl">
          <p className="font-sans text-xs font-bold uppercase tracking-[0.2em] text-zi-accent">
            Simple, transparent pricing
          </p>
          <h2 className="mt-4 font-heading text-4xl font-bold tracking-tight text-zi-ink sm:text-5xl lg:text-6xl">
            Our Pricing.
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-zi-primary via-[#1E5FD0] to-zi-accent p-8 text-white shadow-xl shadow-zi-primary/25 sm:p-12 lg:col-span-7"
            data-testid="pricing-uk-card"
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-zi-accent/40 blur-3xl" />
            <div className="relative">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 font-sans text-xs font-semibold">
                Our pricing for UK-based companies
              </span>
              <div className="mt-6 flex items-end gap-2">
                <span className="font-heading text-6xl font-bold tracking-tight sm:text-7xl">{PRICING.uk.price}</span>
                <span className="mb-2 font-heading text-lg font-medium text-white/80">{PRICING.uk.period}</span>
              </div>
              <p className="mt-2 font-heading text-lg font-medium text-white/90">{PRICING.uk.note}</p>
              <ul className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {PRICING.uk.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white text-zi-primary">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <span className="font-sans text-sm text-white/95">{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid="pricing-quote-button"
                onClick={() => scrollTo("#contact")}
                className="group mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 font-heading text-base font-semibold text-zi-primary transition-all duration-300 hover:bg-zi-ink hover:text-white"
              >
                Get your bespoke quote
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col justify-between rounded-[2rem] border border-zi-line bg-zi-tint2 p-8 sm:p-10 lg:col-span-5"
            data-testid="pricing-global-card"
          >
            <div>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-zi-primary/10 text-zi-primary">
                <Globe size={24} />
              </span>
              <h3 className="mt-5 font-heading text-2xl font-bold text-zi-ink">India &amp; global companies</h3>
              <p className="mt-3 font-sans text-base leading-relaxed text-zi-muted">
                {PRICING.global}
              </p>
            </div>
            <button
              data-testid="pricing-global-button"
              onClick={() => scrollTo("#contact")}
              className="group mt-8 inline-flex items-center gap-2 font-heading text-base font-semibold text-zi-primary"
            >
              Request a bespoke quote
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
